import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, ClipboardCheck, ArrowRight, Calendar, Mail, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackDemoClick, trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "ch_exit_dismissed";
const DISMISS_DAYS = 7;
const TIME_TRIGGER_SECONDS = 45;

type PopupVariant = "maturity" | "demo" | "audit" | "newsletter";

interface VariantConfig {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  bullets: string[];
  buttonText: string;
  link: string;
  trackingId: string;
}

const VARIANT_CONFIGS: Record<PopupVariant, VariantConfig> = {
  maturity: {
    icon: ClipboardCheck,
    title: "Before You Go...",
    subtitle: "Take 2 minutes to check your compliance readiness",
    description: "Get your free Compliance Scorecard and see exactly where your RTO stands against the 2025 Standards.",
    bullets: [
      "Instant results in under 3 minutes",
      "Identify your top compliance gaps",
      "Get actionable recommendations",
    ],
    buttonText: "Start Free Check",
    link: "/compliance-scorecard/start",
    trackingId: "exit_intent_maturity",
  },
  demo: {
    icon: Calendar,
    title: "See It in Action",
    subtitle: "Book a personalised demo",
    description: "Let us show you how ComplyHub helps RTOs like yours stay audit-ready with less effort.",
    bullets: [
      "30-minute personalised walkthrough",
      "Focus on your compliance pain points",
      "No obligation, just answers",
    ],
    buttonText: "Book a Demo",
    link: "/demo#book",
    trackingId: "exit_intent_demo",
  },
  audit: {
    icon: Shield,
    title: "Audit Coming Up?",
    subtitle: "Check your readiness first",
    description: "See how your RTO compares against the 2025 Standards before the auditor does.",
    bullets: [
      "Identify evidence gaps early",
      "Organise your audit pack",
      "Build auditor confidence",
    ],
    buttonText: "See Audit Readiness",
    link: "/auditors",
    trackingId: "exit_intent_audit",
  },
  newsletter: {
    icon: Mail,
    title: "Stay Compliance-Ready",
    subtitle: "Get updates that matter",
    description: "Join 500+ RTO professionals who get our practical compliance insights and standards updates.",
    bullets: [
      "Monthly compliance tips",
      "Standards update alerts",
      "Free resources and templates",
    ],
    buttonText: "Get Updates",
    link: "/resources",
    trackingId: "exit_intent_newsletter",
  },
};

function getVariantForRoute(pathname: string): PopupVariant {
  // Product/pricing/features pages → Demo
  if (
    pathname.startsWith("/product") || 
    pathname.startsWith("/pricing") || 
    pathname.startsWith("/features")
  ) {
    return "demo";
  }
  // Auditors/compliance pages → Audit readiness
  if (pathname.startsWith("/auditors") || pathname.startsWith("/compliance")) {
    return "audit";
  }
  // Blog pages → Newsletter
  if (pathname.startsWith("/blog") || pathname.match(/^\/[a-z]+-[a-z]+/)) {
    return "newsletter";
  }
  // Default → Maturity check
  return "maturity";
}

/**
 * ExitIntentPopup - Shows a lead capture popup when user shows exit intent
 * Triggers on: mouse leaving viewport (desktop), tab blur (mobile), or 45s timer (first visit)
 * Contextual messaging based on current page
 */
export default function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const location = useLocation();

  const variant = getVariantForRoute(location.pathname);
  const config = VARIANT_CONFIGS[variant];

  // Check if user dismissed recently
  const isDismissed = useCallback(() => {
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (!dismissedAt) return false;
    
    const dismissedTime = parseInt(dismissedAt, 10);
    const daysSince = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
  }, []);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    trackEvent("exit_intent_dismiss", { variant });
  }, [variant]);

  // Handle CTA click
  const handleCtaClick = useCallback(() => {
    if (variant === "demo") {
      trackDemoClick(config.trackingId);
    } else {
      trackEvent("exit_intent_cta_click", { variant, destination: config.link });
    }
    handleDismiss();
  }, [variant, config.trackingId, config.link, handleDismiss]);

  useEffect(() => {
    if (isDismissed() || hasTriggered) return;

    const triggerPopup = (source: string) => {
      if (hasTriggered || isDismissed()) return;
      setIsVisible(true);
      setHasTriggered(true);
      trackEvent("exit_intent_trigger", { variant, source });
    };

    // Desktop: mouse leaves viewport from top
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        triggerPopup("mouse_leave");
      }
    };

    // Mobile: visibility change (tab switch)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Mark as triggered for when they return, but don't show immediately
        setHasTriggered(true);
      }
    };

    // Time-based trigger: 45 seconds for first-time visitors
    const isFirstVisit = !sessionStorage.getItem("ch_session_started");
    if (isFirstVisit) {
      sessionStorage.setItem("ch_session_started", "true");
    }

    let timeoutId: NodeJS.Timeout | null = null;

    // Only trigger after user has been on page for 5 seconds (for mouse/visibility)
    // Time trigger runs independently
    const setupTimeout = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }, 5000);

    // Time-based trigger at 45 seconds (only for first visit sessions)
    if (isFirstVisit) {
      timeoutId = setTimeout(() => {
        triggerPopup("time_trigger");
      }, TIME_TRIGGER_SECONDS * 1000);
    }

    return () => {
      clearTimeout(setupTimeout);
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isDismissed, hasTriggered, variant]);

  if (!isVisible) return null;

  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-ch-acai/60 backdrop-blur-sm animate-fade-in"
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl animate-scale-in overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-ch-acai/10 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-ch-acai/60" />
        </button>

        {/* Header with gradient */}
        <div className="bg-ch-hero px-6 py-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <IconComponent className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">
            {config.title}
          </h3>
          <p className="text-white/90 text-sm">
            {config.subtitle}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-ch-acai/80 text-center mb-6">
            {config.description}
          </p>

          <ul className="space-y-3 mb-6">
            {config.bullets.map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-ch-acai">
                <span className="w-5 h-5 rounded-full bg-ch-cyan/15 flex items-center justify-center flex-shrink-0">
                  <span className="w-2 h-2 rounded-full bg-ch-cyan" />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <Button
            className="w-full bg-ch-cyan text-white hover:bg-ch-cyan/90 font-semibold h-12 rounded-xl"
            asChild
          >
            <Link to={config.link} onClick={handleCtaClick}>
              {config.buttonText}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>

          <button
            onClick={handleDismiss}
            className="w-full mt-3 text-sm text-ch-acai/60 hover:text-ch-acai/80 transition-colors py-2"
          >
            No thanks, I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}
